'use client';
import React, { useState } from 'react';
import { PLAYER_COLORS, getPieceDistribution, type AIDifficulty } from '@/lib/gameEngine';
import type { PlayerConfig } from './GameSetupClient';

type Props = {
  players: PlayerConfig[];
  onUpdatePlayers: (players: PlayerConfig[]) => void;
  onNext: () => void;
  onBack: () => void;
};

const DIFFICULTY_LABELS: Record<AIDifficulty, { label: string; desc: string; color: string }> = {
  easy: { label: 'Easy', desc: 'Random moves', color: '#10B981' },
  medium: { label: 'Medium', desc: 'Blocks threats', color: '#F59E0B' },
  hard: { label: 'Hard', desc: 'Looks ahead', color: '#DC2626' },
};

export default function PlayerConfigStep({ players, onUpdatePlayers, onNext, onBack }: Props) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updatePlayer = (index: number, updates: Partial<PlayerConfig>) => {
    const updated = players.map((p, i) => (i === index ? { ...p, ...updates } : p));
    onUpdatePlayers(updated);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    players.forEach((p, i) => {
      if (!p.name.trim()) newErrors[`name-${i}`] = 'Player name is required';
      if (p.name.trim().length > 16) newErrors[`name-${i}`] = 'Max 16 characters';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  const distribution = getPieceDistribution(players.length);

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h2 className="text-hero-md text-foreground mb-3">Configure Players</h2>
        <p className="text-muted-foreground">
          Set names, toggle AI opponents, and assign colors.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {players.map((player, index) => {
          const color = PLAYER_COLORS[player.colorIndex];
          return (
            <div
              key={player.id}
              className="rounded-2xl border border-border p-5 transition-all duration-200"
              style={{
                background: 'var(--card)',
                borderColor: player.isAI ? `${color.color}40` : 'var(--border)',
              }}
            >
              <div className="flex items-start gap-4">
                {/* Color swatch */}
                <div
                  className="w-10 h-10 rounded-xl flex-shrink-0 mt-1"
                  style={{ background: color.color, boxShadow: `0 0 12px ${color.color}50` }}
                />

                <div className="flex-1 flex flex-col gap-3">
                  {/* Name + pieces */}
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-600 text-muted-foreground uppercase tracking-wider mb-1.5">
                        Player {index + 1} Name
                      </label>
                      <input
                        type="text"
                        value={player.name}
                        onChange={(e) => updatePlayer(index, { name: e.target.value })}
                        placeholder={`Player ${index + 1}`}
                        maxLength={16}
                        className={`w-full px-3 py-2.5 rounded-xl bg-input border text-foreground text-sm font-500
                                    focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                          errors[`name-${index}`] ? 'border-red-500' : 'border-border'
                        }`}
                      />
                      {errors[`name-${index}`] && (
                        <p className="text-red-400 text-xs mt-1">{errors[`name-${index}`]}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <div className="text-xs text-muted-foreground font-500 mb-1">Pieces</div>
                      <div
                        className="font-mono-game text-2xl font-700"
                        style={{ color: color.color }}
                      >
                        {distribution[index]}
                      </div>
                    </div>
                  </div>

                  {/* AI toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-600 text-foreground">AI Opponent</span>
                      <span className="text-xs text-muted-foreground">
                        {player.isAI ? 'Controlled by AI engine' : 'Human player'}
                      </span>
                    </div>
                    <button
                      onClick={() => updatePlayer(index, { isAI: !player.isAI })}
                      className={`relative w-12 h-6 rounded-full transition-all duration-300 ${
                        player.isAI ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${
                          player.isAI ? 'left-6' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  {/* AI difficulty */}
                  {player.isAI && (
                    <div>
                      <label className="block text-xs font-600 text-muted-foreground uppercase tracking-wider mb-2">
                        Difficulty
                      </label>
                      <div className="flex gap-2">
                        {(['easy', 'medium', 'hard'] as AIDifficulty[]).map((diff) => {
                          const info = DIFFICULTY_LABELS[diff];
                          const isSelected = player.aiDifficulty === diff;
                          return (
                            <button
                              key={`diff-${player.id}-${diff}`}
                              onClick={() => updatePlayer(index, { aiDifficulty: diff })}
                              className={`flex-1 py-2 px-3 rounded-xl text-xs font-600 border transition-all duration-150 ${
                                isSelected ? 'border-current' : 'border-border bg-muted/50 text-muted-foreground hover:text-foreground'
                              }`}
                              style={isSelected ? { color: info.color, background: `${info.color}15`, borderColor: `${info.color}60` } : {}}
                            >
                              {info.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex gap-3 mt-8">
        <button
          onClick={onBack}
          className="flex-1 py-3.5 rounded-2xl border border-border text-foreground font-600 hover:bg-secondary/50 active:scale-95 transition-all duration-150"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="flex-2 flex-grow py-3.5 rounded-2xl bg-primary text-primary-foreground font-700 hover:bg-primary/90 active:scale-95 transition-all duration-150 glow-primary"
        >
          Configure Rules →
        </button>
      </div>
    </div>
  );
}